/** @prettier */

/**
 * 編集ダイアログ
 */

import {
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Stack,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import TextFieldSelectBox from '../../component/SelectBox';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
//API import
import {patchSettingUser} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {pullDownMenu, format} from '../../define';

/**
 * 圧力単位切り替えのダイアログ
 * @param props props
 * @returns JSX.Element
 */
export const UnitEditDialog = (props: {
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 単位 */
  unit: apiIF.res.getSettingUser['unit'];
  /** 単位（set関数） */
  setUnit: React.Dispatch<
    React.SetStateAction<apiIF.res.getSettingUser['unit']>
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数

  const [pressureUnit, setPressureUnit] = useState(props.unit.pressure); //圧力単位
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * 単位変更時
   * @param event
   * @param newUnit
   */
  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newUnit: apiIF.res.getSettingUser['unit']['pressure'] | null
  ) => {
    if (newUnit !== null) {
      setPressureUnit(newUnit);
    }
  };
  /**
   * OKボタン押下時処理
   */
  const handleOK = () => {
    setAPIWait(true);
    if (pressureUnit === undefined) return;
    patchSettingUser({unit: {pressure: pressureUnit}})
      .then(response => {
        handleClose();
      })
      .catch(err => {
        apiErrorAlert(trans);
      })
      .finally(() => {
        setAPIWait(false);
        window.location.reload();
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setPressureUnit(props.unit?.pressure);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="unit-dialog-title">
        <DialogTitle id="unit-dialog-title">
          {trans('WebsiteSettingEditDialog.unit.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {/* ダイアログに表示するメッセージ */}
            <DialogContentText className="accountSetting__dialog__content__text">
              {trans('WebsiteSettingEditDialog.unit.msg')}
            </DialogContentText>
            <Box className="accountSetting__dialog__content__toggleButton">
              <ToggleButtonGroup
                value={pressureUnit}
                exclusive
                onChange={handleChange}
                aria-label="Display units">
                <ToggleButton title="MPa" value="MPa" aria-label="MPa">
                  {trans('WebsiteSettingEditDialog.toggleButton.mpa')}
                </ToggleButton>
                <ToggleButton title="bar" value="bar" aria-label="bar">
                  {trans('WebsiteSettingEditDialog.toggleButton.bar')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('WebsiteSettingEditDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={handleOK}
            buttonText={trans('WebsiteSettingEditDialog.buttonTxt.ok')}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};

/**
 * ユーザ所属国設定ダイアログ
 * @param props props
 * @returns JSX.Element
 */
export const CountryEditDialog = (props: {
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** ユーザ所属国 */
  country: apiIF.res.getSettingUser['country'];
  /** 支払いユーザ判定 */
  payingUser: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //prettier-ignore
  const [country, setCountry] = useState(props.country); //ユーザ所在国
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * OKボタン押下時処理
   */
  const handleOK = () => {
    setAPIWait(true);
    if (country === '') return;
    //ユーザ情報修正
    patchSettingUser({country: country})
      .then(response => {
        handleClose();
      })
      .catch(err => {
        apiErrorAlert(trans);
      })
      .finally(() => {
        setAPIWait(false);
        window.location.reload();
      });
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setCountry(props.country);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="country-dialog-title">
        <DialogTitle id="country-dialog-title">
          {trans('WebsiteSettingEditDialog.country.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {/* ダイアログに表示するメッセージ */}
            <DialogContentText className="accountSetting__dialog__content__text">
              {trans('WebsiteSettingEditDialog.country.msg.instruction')}
              {/* 支払いユーザの場合、ユーザ所属国変更不可のメッセージを表示 */}
              {props.payingUser && (
                <Box
                  component="span"
                  className="accountSetting__dialog__content__text--error">
                  {`\n${trans('WebsiteSettingEditDialog.country.msg.billing')}`}
                </Box>
              )}
            </DialogContentText>
            {/* ユーザ所属国選択ボックス */}
            <TextFieldSelectBox
              choices={pullDownMenu.USER_COUNTRY_OF_INCORPORATION}
              setValue={setCountry}
              id={'countrySelect'}
              disabled={props.payingUser}
              class="accountSetting__dialog__content__selectBox"
              label={trans('WebsiteSettingEditDialog.country.textBox.label')}
              value={country}
              selectClass="accountSetting__dialog__content__selectBox__select"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('WebsiteSettingEditDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={handleOK}
            buttonText={trans('WebsiteSettingEditDialog.buttonTxt.ok')}
            disabled={props.payingUser || country === ''}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};

/**
 * フッター修正ダイアログ
 * @param props props
 * @returns JSX.Element
 */
export const FooterEditDialog = (props: {
  /** フッター */
  footer: string;
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  const [footerText, setFooterText] = useState(props.footer); //テキストデータ
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [footerError, setFooterError] = useState(false); //パスワード

  //フッターのエラーチェック
  useEffect(() => {
    if (footerText === '' || footerError) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [footerText, footerError]);

  /**
   * OKボタン押下時処理
   */
  const uploadImage = () => {
    setOKBtnDisabled(true);
    setAPIWait(true);

    const body: apiIF.reqParameters.patchSettingUser = {
      'footer-text': footerText,
    };
    patchSettingUser(body)
      .then(() => {
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
        window.location.reload();
      });
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setFooterText(props.footer);
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="footer-dialog-title"
        maxWidth="md">
        <DialogTitle id="footer-dialog-title">
          {trans('WebsiteSettingUploadDialog.footer.title')}
        </DialogTitle>
        {/* 入力欄 */}
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText className="accountSetting__dialog__content__text">
              {trans('WebsiteSettingUploadDialog.footer.msg')}
            </DialogContentText>
            <Box className="accountSetting__dialog__content__textBox">
              <ChkInputTextbox
                setValue={setFooterText}
                setError={setFooterError}
                chkLength={format.FOOTER_LENGTH_MAX}
                type="text"
                label={trans(
                  'WebsiteSettingUploadDialog.footer.txtField.label'
                )}
                value={footerText}
                id="mv-footerTextInput"
                helperText=" "
                errHelperText={trans(
                  'WebsiteSettingUploadDialog.footer.txtField.errHelperTxt',
                  {length: format.FOOTER_LENGTH_MAX}
                )}
                variant="standard"
                fullWidth={true}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('WebsiteSettingUploadDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={uploadImage}
            buttonText={trans('WebsiteSettingUploadDialog.buttonTxt.ok')}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};
